import React, { useState, useEffect, useRef } from 'react';
import { 
  MapPin, 
  Layers, 
  Filter, 
  AlertTriangle, 
  Loader2,
  Trash2,
  Droplets,
  TreePine,
  Construction,
  Flame,
  HelpCircle,
  Eye,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  Clock,
  X
} from 'lucide-react';

interface IssueReport {
  id: string;
  category: string;
  description: string;
  location: {
    latitude: number;
    longitude: number;
    address: string;
  };
  status: 'submitted' | 'verified' | 'sent_to_authority' | 'resolved' | 'rejected';
  created_at: string;
  upvotes: number;
}

const ISSUE_CATEGORIES = [
  { 
    id: 'waste_dump', 
    label: 'Waste Dump', 
    icon: Trash2, 
    color: 'text-orange-600',
    bg: 'bg-orange-100',
    markerColor: '#f97316'
  },
  { 
    id: 'illegal_tree_cutting', 
    label: 'Illegal Tree Cutting', 
    icon: TreePine, 
    color: 'text-green-600',
    bg: 'bg-green-100',
    markerColor: '#16a34a'
  },
  { 
    id: 'water_contamination', 
    label: 'Water Contamination', 
    icon: Droplets, 
    color: 'text-blue-600',
    bg: 'bg-blue-100',
    markerColor: '#2563eb'
  },
  { 
    id: 'construction_pollution', 
    label: 'Construction Pollution', 
    icon: Construction, 
    color: 'text-yellow-600',
    bg: 'bg-yellow-100',
    markerColor: '#ca8a04'
  },
  { 
    id: 'open_burning', 
    label: 'Open Burning', 
    icon: Flame, 
    color: 'text-red-600',
    bg: 'bg-red-100',
    markerColor: '#dc2626'
  },
  { 
    id: 'other', 
    label: 'Other', 
    icon: HelpCircle, 
    color: 'text-purple-600',
    bg: 'bg-purple-100',
    markerColor: '#9333ea'
  }
];

// Mock data for demonstration
const mockIssueReports: IssueReport[] = [
  {
    id: 'issue-1',
    category: 'waste_dump',
    description: 'Large pile of trash dumped by the riverside. Includes plastic waste and potentially hazardous materials.',
    location: {
      latitude: 37.7749,
      longitude: -122.4194,
      address: '123 River Road'
    },
    status: 'verified',
    created_at: '2025-06-10T14:30:00Z',
    upvotes: 12
  },
  {
    id: 'issue-2',
    category: 'water_contamination',
    description: 'Stream appears to be contaminated with chemical runoff. Water has an unusual color and odor.',
    location: {
      latitude: 37.7833,
      longitude: -122.4167,
      address: '456 Creek View'
    },
    status: 'sent_to_authority',
    created_at: '2025-06-09T10:15:00Z',
    upvotes: 28
  },
  {
    id: 'issue-3',
    category: 'illegal_tree_cutting',
    description: 'Several trees being cut down in protected forest area without permits.',
    location: {
      latitude: 37.7850,
      longitude: -122.4300,
      address: '789 Forest Lane'
    },
    status: 'resolved',
    created_at: '2025-06-05T08:45:00Z',
    upvotes: 45
  },
  {
    id: 'issue-4',
    category: 'construction_pollution',
    description: 'Construction site releasing dust and debris into the air and nearby stream.',
    location: {
      latitude: 37.7700,
      longitude: -122.4100,
      address: '101 Build Site'
    },
    status: 'submitted',
    created_at: '2025-06-11T11:30:00Z',
    upvotes: 3
  },
  {
    id: 'issue-5',
    category: 'open_burning',
    description: 'Open burning of waste materials causing air pollution in residential area.',
    location: {
      latitude: 37.7650,
      longitude: -122.4250,
      address: '202 Smoke Avenue'
    },
    status: 'verified',
    created_at: '2025-06-08T16:20:00Z',
    upvotes: 17
  }
];

export default function IssueReportMap() {
  const [issueReports, setIssueReports] = useState<IssueReport[]>(mockIssueReports);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedReport, setSelectedReport] = useState<IssueReport | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [userLocation, setUserLocation] = useState<{latitude: number, longitude: number} | null>(null);
  
  const mapContainerRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => {
      setLoading(false);
      getUserLocation();
    }, 1000);
    
    return () => clearTimeout(timer);
  }, []);
  
  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.error('Error getting user location:', error);
          // Default to San Francisco if location access is denied
          setUserLocation({
            latitude: 37.7749,
            longitude: -122.4194
          });
        }
      );
    } else {
      console.log('Geolocation is not supported by this browser.');
      // Default to San Francisco
      setUserLocation({
        latitude: 37.7749,
        longitude: -122.4194
      });
    }
  };
  
  const toggleCategoryFilter = (category: string) => {
    setCategoryFilter(prev => 
      prev.includes(category)
        ? prev.filter(c => c !== category)
        : [...prev, category]
    );
  };
  
  const toggleStatusFilter = (status: string) => {
    setStatusFilter(prev => 
      prev.includes(status)
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };
  
  const filteredReports = issueReports.filter(report => {
    const matchesCategory = categoryFilter.length === 0 || categoryFilter.includes(report.category);
    const matchesStatus = statusFilter.length === 0 || statusFilter.includes(report.status);
    return matchesCategory && matchesStatus;
  });
  
  const getCategoryInfo = (category: string) => {
    return ISSUE_CATEGORIES.find(c => c.id === category) || ISSUE_CATEGORIES[5]; // Default to "other"
  };
  
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-yellow-100 text-yellow-800';
      case 'verified': return 'bg-blue-100 text-blue-800';
      case 'sent_to_authority': return 'bg-purple-100 text-purple-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };
  
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'submitted': return 'Submitted';
      case 'verified': return 'Verified';
      case 'sent_to_authority': return 'Sent to Authority';
      case 'resolved': return 'Resolved';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };
  
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading map data...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Environmental Issue Map</h2>
        <p className="text-gray-600">
          View reported environmental issues on an interactive map
        </p>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center space-x-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <span className="font-medium text-red-800">Error</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
        </div>
      )}
      
      {/* Map Container */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Map Controls */}
        <div className="p-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <Filter className="h-5 w-5" />
              <span>Filters</span>
              {showFilters ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            
            <button
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <Layers className="h-5 w-5" />
              <span>Layers</span>
            </button>
          </div>
          
          <div className="flex items-center space-x-2">
            <span className="text-gray-600 text-sm">
              Showing {filteredReports.length} of {issueReports.length} issues
            </span>
            
            {(categoryFilter.length > 0 || statusFilter.length > 0) && (
              <button
                onClick={() => {
                  setCategoryFilter([]);
                  setStatusFilter([]);
                }}
                className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
        
        {/* Filters Panel */}
        {showFilters && (
          <div className="p-4 border-b border-gray-200 bg-gray-50">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Category Filters */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Filter by Category</h4>
                <div className="space-y-2">
                  {ISSUE_CATEGORIES.map(category => (
                    <label key={category.id} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={categoryFilter.includes(category.id)}
                        onChange={() => toggleCategoryFilter(category.id)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <div className="flex items-center space-x-2">
                        <div className={`p-1 rounded-md ${category.bg}`}>
                          {React.createElement(category.icon, {
                            className: `h-4 w-4 ${category.color}`
                          })}
                        </div>
                        <span className="text-gray-700">{category.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Status Filters */}
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Filter by Status</h4>
                <div className="space-y-2">
                  {['submitted', 'verified', 'sent_to_authority', 'resolved', 'rejected'].map(status => (
                    <label key={status} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={statusFilter.includes(status)}
                        onChange={() => toggleStatusFilter(status)}
                        className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(status)}`}>
                        {getStatusLabel(status)}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Map View */}
        <div className="relative">
          {/* Map Placeholder - In a real app, this would be replaced with a map library like Google Maps, Mapbox, or Leaflet */}
          <div 
            ref={mapContainerRef}
            className="h-[500px] bg-gray-100 relative"
          >
            {/* Placeholder Map UI */}
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <MapPin className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">
                  Interactive map would be displayed here
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  Using Mapbox, Google Maps, or Leaflet
                </p>
              </div>
            </div>
            
            {/* Simulated Map Markers */}
            {filteredReports.map(report => {
              const categoryInfo = getCategoryInfo(report.category);
              const Icon = categoryInfo.icon;
              
              // Calculate random positions for demo
              const top = Math.floor(Math.random() * 80) + 10; // 10-90%
              const left = Math.floor(Math.random() * 80) + 10; // 10-90%
              
              return (
                <div
                  key={report.id}
                  className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                  style={{ top: `${top}%`, left: `${left}%` }}
                  onClick={() => setSelectedReport(report)}
                >
                  <div className={`p-2 rounded-full ${categoryInfo.bg} hover:scale-110 transition-transform`}>
                    <Icon className={`h-6 w-6 ${categoryInfo.color}`} />
                  </div>
                </div>
              );
            })}
            
            {/* User Location Marker */}
            {userLocation && (
              <div
                className="absolute transform -translate-x-1/2 -translate-y-1/2"
                style={{ top: '50%', left: '50%' }}
              >
                <div className="p-2 rounded-full bg-blue-500 border-4 border-white shadow-lg">
                  <div className="h-2 w-2 bg-white rounded-full"></div>
                </div>
                <div className="text-xs font-medium text-blue-800 bg-white px-2 py-0.5 rounded shadow-sm mt-1">
                  You are here
                </div>
              </div>
            )}
          </div>
          
          {/* Map Legend */}
          <div className="absolute bottom-4 left-4 bg-white rounded-lg shadow-md p-3 max-w-xs">
            <h4 className="font-medium text-gray-900 mb-2 text-sm">Legend</h4>
            <div className="space-y-1">
              {ISSUE_CATEGORIES.map(category => (
                <div key={category.id} className="flex items-center space-x-2">
                  <div className={`p-1 rounded-full ${category.bg}`}>
                    {React.createElement(category.icon, {
                      className: `h-3 w-3 ${category.color}`
                    })}
                  </div>
                  <span className="text-xs text-gray-700">{category.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Selected Issue Details */}
      {selectedReport && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`p-2 rounded-lg ${getCategoryInfo(selectedReport.category).bg}`}>
                {React.createElement(getCategoryInfo(selectedReport.category).icon, {
                  className: `h-5 w-5 ${getCategoryInfo(selectedReport.category).color}`
                })}
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{getCategoryInfo(selectedReport.category).label}</h3>
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedReport.status)}`}>
                    {getStatusLabel(selectedReport.status)}
                  </span>
                  <span>•</span>
                  <span>{formatDate(selectedReport.created_at)}</span>
                  <span>•</span>
                  <span>{selectedReport.upvotes} upvotes</span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedReport(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          <p className="text-gray-700 mb-4">{selectedReport.description}</p>
          
          <div className="flex items-center space-x-2 text-gray-600">
            <MapPin className="h-4 w-4" />
            <span>{selectedReport.location.address}</span>
          </div>
          
          <div className="mt-4 flex space-x-3">
            <button
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <Eye className="h-4 w-4" />
              <span>View Full Details</span>
            </button>
            
            <button
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-medium transition-colors flex items-center space-x-2"
            >
              <MapPin className="h-4 w-4" />
              <span>Get Directions</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Issue Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-emerald-100 p-2 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{issueReports.length}</p>
              <p className="text-sm text-gray-600">Total Reports</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {issueReports.filter(r => r.status === 'resolved').length}
              </p>
              <p className="text-sm text-gray-600">Resolved</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {issueReports.filter(r => r.status === 'submitted' || r.status === 'verified').length}
              </p>
              <p className="text-sm text-gray-600">In Progress</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <MapPin className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {Object.keys(
                  issueReports.reduce((acc, report) => {
                    const key = `${report.location.latitude.toFixed(2)},${report.location.longitude.toFixed(2)}`;
                    acc[key] = true;
                    return acc;
                  }, {} as Record<string, boolean>)
                ).length}
              </p>
              <p className="text-sm text-gray-600">Unique Locations</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}